import { Building2, Eye, Filter, Globe, Mail, Pencil, Phone, Plus, Search, Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ICompany, CompanyStatus, CompanySize, FundingStage } from "../../../interfaces/company";
import {
    getAdminCompanies,
    deleteAdminCompany,
    getAdminCompanyStats,
    getAllCompanyFilterValues,
    AdminCompanyFilters,
    CompanyStats,
    FilterValues,
} from "../../../interfaces/admin/companies";
import BONavigation from "../../../components/admin/BONavigation";

export default function AdminCompanies() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState<ICompany[]>([]);
    const [stats, setStats] = useState<CompanyStats | null>(null);
    const [filterValues, setFilterValues] = useState<FilterValues>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const [filters, setFilters] = useState<AdminCompanyFilters>({
        sortBy: "createdAt",
        order: "desc",
    });

    useEffect(() => {
        fetchData();
        fetchStats();
        fetchFilterValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, page]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getAdminCompanies({ 
                ...filters, 
                search: searchTerm || undefined, 
                page, 
                limit: 20 
            });
            
            if (page === 1) {
                setCompanies(response.data || []);
            } else {
                setCompanies(prev => [...prev, ...(response.data || [])]);
            }
            
            setHasMore(response.data?.length === 20);
        } catch (err) {
            console.error("Failed to fetch companies:", err);
            setError("Failed to load companies");
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const statsData = await getAdminCompanyStats();
            setStats(statsData);
        } catch (err) {
            console.error("Failed to fetch stats:", err);
        }
    };

    const fetchFilterValues = async () => {
        try {
            const values = await getAllCompanyFilterValues();
            setFilterValues(values);
        } catch (err) {
            console.error("Failed to fetch filter values:", err);
        }
    };

    const handleSearch = () => {
        setPage(1);
        fetchData();
    };

    const handleFilterChange = (key: keyof AdminCompanyFilters, value: any) => {
        setPage(1);
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleDelete = async (companyId: string) => {
        if (!window.confirm("Are you sure you want to delete this company?")) {
            return;
        }

        try {
            await deleteAdminCompany(companyId);
            setCompanies(prev => prev.filter(c => c._id !== companyId));
            fetchStats();
        } catch (err) {
            console.error("Failed to delete company:", err);
            alert("Failed to delete company");
        }
    };

    const loadMore = () => {
        setPage(prev => prev + 1);
    };

    const statusOptions: { value: CompanyStatus; label: string; color: string }[] = [
        { value: "prospect", label: "Prospect", color: "bg-gray-100 text-gray-700 border-gray-200" },
        { value: "contacted", label: "Contacted", color: "bg-yellow-50 text-yellow-800 border-yellow-200" },
        { value: "qualified", label: "Qualified", color: "bg-purple-50 text-purple-800 border-purple-200" },
        { value: "client", label: "Client", color: "bg-green-50 text-green-800 border-green-200" },
        { value: "inactive", label: "Inactive", color: "bg-gray-50 text-gray-800 border-gray-200" },
    ];

    const sizeOptions: { value: CompanySize; label: string }[] = [
        { value: "1-10", label: "1-10 employees" },
        { value: "11-50", label: "11-50 employees" },
        { value: "51-200", label: "51-200 employees" },
        { value: "201-500", label: "201-500 employees" },
        { value: "500+", label: "500+ employees" },
        { value: "1000+", label: "1000+ employees" },
    ];

    const fundingStageOptions: { value: FundingStage; label: string }[] = [
        { value: "bootstrap", label: "Bootstrap" },
        { value: "seed", label: "Seed" },
        { value: "series_a", label: "Series A" },
        { value: "series_b", label: "Series B" },
        { value: "series_c", label: "Series C" },
        { value: "series_d", label: "Series D" },
        { value: "ipo", label: "IPO" },
        { value: "acquired", label: "Acquired" },
    ];

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", { 
            month: "short", 
            day: "numeric", 
            year: "numeric" 
        });
    };

    const getStatusColor = (status?: CompanyStatus) => {
        return statusOptions.find(s => s.value === status)?.color || "bg-gray-50 text-gray-800 border-gray-200";
    };

    const getPrimaryContact = (company: ICompany) => {
        return company.contacts?.find(c => c.isPrimary) || company.contacts?.[0];
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-black dark:text-white">Company Management</h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Admin interface to create and manage companies
                            </p>
                        </div>
                        <button
                            onClick={() => navigate("/bo/companies/create")}
                            className="flex text-xs items-center gap-2 bg-black text-white px-3 py-1.5 rounded-xs hover:bg-gray-800 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            Create Company
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <BONavigation />

                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Total Companies</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">{stats.totalCompanies}</p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Prospects</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                {stats.byStatus.find(s => s.status === 'prospect')?.count || 0}
                            </p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Clients</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                {stats.byStatus.find(s => s.status === 'client')?.count || 0}
                            </p>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-xs p-4">
                            <p className="text-xs text-gray-600 mb-1">Industries</p>
                            <p className="text-xl font-semibold text-navy-700 dark:text-white">{stats.byIndustry.length}</p>
                        </div>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
                            <Search className="w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search companies, contacts, SIREN..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                                className="flex-1 border-0 focus:ring-0 focus:outline-none text-sm placeholder:text-gray-400"
                            />
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xs border transition-colors ${
                                    showFilters
                                        ? "bg-black text-white border-black"
                                        : "bg-white text-gray-700 border-gray-200 hover:bg-[#F9F6F3]"
                                }`}
                            >
                                <Filter className="w-4 h-4" />
                                Filters
                            </button>
                        </div>
                    </div>

                    {/* Filter Panel */}
                    {showFilters && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Status</label>
                                    <select
                                        value={filters.status || ""}
                                        onChange={(e) => handleFilterChange("status", e.target.value || undefined)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="">All Statuses</option>
                                        {statusOptions.map(status => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Industry</label>
                                    <select
                                        value={filters.industry || ""}
                                        onChange={(e) => handleFilterChange("industry", e.target.value || undefined)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="">All Industries</option>
                                        {filterValues.industries?.map(industry => (
                                            <option key={industry} value={industry}>
                                                {industry}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Size</label>
                                    <select
                                        value={filters.size || ""}
                                        onChange={(e) => handleFilterChange("size", e.target.value || undefined)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="">All Sizes</option>
                                        {sizeOptions.map(size => (
                                            <option key={size.value} value={size.value}>
                                                {size.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Funding Stage</label>
                                    <select
                                        value={filters.fundingStage || ""}
                                        onChange={(e) => handleFilterChange("fundingStage", e.target.value || undefined)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="">All Stages</option>
                                        {fundingStageOptions.map(stage => (
                                            <option key={stage.value} value={stage.value}>
                                                {stage.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Sort By</label>
                                    <select
                                        value={filters.sortBy || "createdAt"}
                                        onChange={(e) => handleFilterChange("sortBy", e.target.value)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="createdAt">Created Date</option>
                                        <option value="name">Company Name</option>
                                        <option value="industry">Industry</option>
                                        <option value="status">Status</option>
                                        <option value="nextActionAt">Next Action</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Order</label>
                                    <select
                                        value={filters.order || "desc"}
                                        onChange={(e) => handleFilterChange("order", e.target.value)}
                                        className="w-full px-2.5 py-1.5 border border-gray-200 rounded-xs bg-white text-gray-900 text-xs focus:ring-1 focus:ring-gray-900 focus:outline-none"
                                    >
                                        <option value="desc">Newest First</option>
                                        <option value="asc">Oldest First</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Companies Table */}
                {loading && page === 1 ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-gray-500 text-sm">Loading companies...</div>
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-red-600 text-sm">{error}</div>
                    </div>
                ) : companies.length === 0 ? (
                    <div className="text-center py-16">
                        <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-base font-semibold text-navy-700 dark:text-white mb-2">
                            No companies found
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            Create your first company to get started
                        </p>
                        <button
                            onClick={() => navigate("/bo/companies/create")}
                            className="bg-black text-white px-4 py-2 text-xs rounded-xs hover:bg-gray-800 transition-colors"
                        >
                            Create Company
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="bg-white border border-gray-200 rounded-xs overflow-hidden">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Company
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Industry
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Size
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Primary Contact
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-4 py-3 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {companies.map((company) => {
                                        const primaryContact = getPrimaryContact(company);
                                        return (
                                            <tr key={company._id} className="hover:bg-[#F9F6F3] transition-colors">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        {company.imageUrl ? (
                                                            <img
                                                                src={company.imageUrl}
                                                                alt={company.name}
                                                                className="h-10 w-10 object-cover rounded-xs border border-gray-200"
                                                                onError={(e) => {
                                                                    e.currentTarget.style.display = 'none';
                                                                }}
                                                            />
                                                        ) : (
                                                            <div className="h-10 w-10 bg-gray-100 rounded-xs border border-gray-200 flex items-center justify-center">
                                                                <Building2 className="w-5 h-5 text-gray-400" />
                                                            </div>
                                                        )}
                                                        <div className="flex flex-col max-w-xs">
                                                            <span className="text-xs font-semibold text-navy-700 dark:text-white truncate">
                                                                {company.name}
                                                            </span>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <span className="text-[11px] text-gray-500 font-mono">
                                                                    {company.siren}
                                                                </span>
                                                                {company.website && (
                                                                    <a
                                                                        href={company.website}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-gray-900 hover:underline"
                                                                    >
                                                                        <Globe className="w-3 h-3" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-xs text-gray-900">
                                                        {company.industry || "—"}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className="text-xs text-gray-900">
                                                        {company.size ? sizeOptions.find(s => s.value === company.size)?.label : "—"}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {primaryContact ? (
                                                        <div className="flex flex-col max-w-xs">
                                                            <span className="text-xs font-medium text-gray-900 truncate">
                                                                {primaryContact.name || `${primaryContact.firstName || ''} ${primaryContact.lastName || ''}`.trim()}
                                                            </span>
                                                            {primaryContact.title && (
                                                                <span className="text-[11px] text-gray-500 truncate">
                                                                    {primaryContact.title}
                                                                </span>
                                                            )}
                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                {primaryContact.email && (
                                                                    <a
                                                                        href={`mailto:${primaryContact.email}`}
                                                                        className="text-gray-900 hover:underline"
                                                                        title={primaryContact.email}
                                                                    >
                                                                        <Mail className="w-3 h-3" />
                                                                    </a>
                                                                )}
                                                                {primaryContact.phone && (
                                                                    <a
                                                                        href={`tel:${primaryContact.phone}`}
                                                                        className="text-green-600 hover:text-green-800"
                                                                        title={primaryContact.phone}
                                                                    >
                                                                        <Phone className="w-3 h-3" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">No contact</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-0.5 text-[11px] font-medium rounded-sm border ${getStatusColor(company.status)}`}>
                                                        {statusOptions.find(s => s.value === company.status)?.label || company.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                                                    {formatDate(company.createdAt)}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() => navigate(`/bo/companies/${company._id}`)}
                                                            className="text-gray-900 hover:underline transition-colors"
                                                            title="View Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => navigate(`/bo/companies/edit/${company._id}`)}
                                                            className="text-black hover:text-black/60 transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Pencil className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(company._id)}
                                                            className="text-red-600 hover:text-red-800 transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Load More */}
                        {hasMore && (
                            <div className="flex justify-center mt-6">
                                <button
                                    onClick={loadMore}
                                    disabled={loading}
                                    className="px-4 py-2 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white text-xs rounded-xs transition-colors"
                                >
                                    {loading ? "Loading..." : "Load More"}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}